const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API = `${BASE}/api/v1/training`;

export interface CardData {
  cardIndex: number;
  cardType: string;
  weekNumber: number;
  questionId: string;
  title?: string;
  body?: string;
  objectives?: string[];
  question?: string;
  options?: { text: string }[];
  prompt?: string;
  minTimeSeconds: number;
  isBlockBoundary?: boolean;
  nextWeek?: number;
  weekReading?: string;
}

export interface StartResponse {
  sessionToken: string;
  sessionId: string;
  resumed: boolean;
  currentCardIndex: number;
  totalCards: number;
  currentCard: CardData;
  employeeName: string;
}

export interface CardResponse {
  currentCardIndex: number;
  totalCards: number;
  currentCard: CardData;
  employeeName: string;
  completed: boolean;
}

export interface SubmitResponse {
  completed: boolean;
  isCorrect?: boolean | null;
  nextCardIndex: number;
  totalCards: number;
  nextCard?: CardData;
}

export async function startTrainingSession(data: {
  fullName: string;
  employeeRef: string;
  email: string;
  branchId: number;
  department: string;
  _hp: string;
  mathAnswer: string;
  mathExpected: number;
}): Promise<StartResponse> {
  const res = await fetch(`${API}/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to start session');
  }
  return res.json();
}

export async function fetchCurrentCard(token: string): Promise<CardResponse> {
  const res = await fetch(`${API}/session/${token}/card`);
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to fetch card');
  }
  return res.json();
}

export async function submitCardResponse(
  token: string,
  payload: {
    questionId: string;
    cardType: string;
    responseText?: string;
    selectedOptionIndex?: number;
    timeSpentSeconds: number;
  }
): Promise<SubmitResponse> {
  const res = await fetch(`${API}/session/${token}/respond`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to submit response');
  }
  return res.json();
}

export async function recordExit(token: string): Promise<void> {
  await fetch(`${API}/session/${token}/exit`, { method: 'POST' }).catch(() => {});
}

export async function fetchAdminDashboard(key: string, branchId?: number) {
  const params = new URLSearchParams({ key });
  if (branchId) params.set('branchId', String(branchId));
  const res = await fetch(`${API}/admin/dashboard?${params}`);
  if (!res.ok) throw new Error('Failed to load dashboard');
  return res.json();
}

export async function fetchEmployeeResponses(sessionId: string, key: string) {
  const res = await fetch(`${API}/admin/session/${sessionId}/responses?key=${key}`);
  if (!res.ok) throw new Error('Failed to load responses');
  return res.json();
}

export async function fetchExitLogs(key: string) {
  const res = await fetch(`${API}/admin/exit-logs?key=${key}`);
  if (!res.ok) throw new Error('Failed to load exit logs');
  return res.json();
}

export async function submitManagerReview(
  sessionId: string,
  key: string,
  data: {
    reviewerName: string;
    overallRating: string;
    followUpRequired: boolean;
    reviewerComments: string;
  }
) {
  const res = await fetch(`${API}/admin/session/${sessionId}/review?key=${key}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to submit review');
  return res.json();
}
